import { useEditor, useNode } from "@craftjs/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import scrollIntoView from "scroll-into-view-if-needed";
import type { Speaker } from "src/types";
import { useBetween } from "use-between";

import { Button } from "@/components/button";
import SettingsTitleWrapper from "@/components/editor/shared/settings-title-wrapper";
import { SwitchInput } from "@/components/shared/forms";
import useAnalytics from "@/hooks/use-analytics";

import { useSpeakerModal } from "../utils";
import {
  BasicRichText,
  Image,
  Link,
  ResizableContainer,
} from "./speaker-card-settings-component-styles";
import speakersTooltipPreview from "./speakers-tooltip-preview.jpg";

export default function SpeakerCardSettings() {
  const { setSpeaker } = useBetween(useSpeakerModal);
  const {
    dom,
    props,
    actions: { setProp },
  } = useNode((node) => {
    return {
      props: node.props as {
        speaker: Speaker;
        isBioAvailable?: boolean;
      },
      dom: node.getDOM(),
      nodeId: node.id,
    };
  });

  const {
    enabled,
    actions: { setOptions },
  } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const { t } = useTranslation();
  const { logAnalytics } = useAnalytics();

  const { speaker } = props;

  const handleToggle = React.useCallback(() => {
    setProp((props) => (props.isBioAvailable = !props.isBioAvailable));
    logAnalytics("CANVAS_EDITOR_SPEAKER_CARD_SETTINGS_VIEWED", {
      toggle_on: !props.isBioAvailable,
    });
  }, [logAnalytics, props.isBioAvailable, setProp]);

  const handleBioTextChange = React.useCallback(
    (html: string) => {
      setProp((props) => (props.speaker.bio = html));
    },
    [setProp]
  );

  const scrollIntoNode = React.useCallback(() => {
    if (dom) {
      window.requestAnimationFrame(() => {
        scrollIntoView(dom, { behavior: "smooth" });
      });
    }
  }, [dom]);

  const handlePreviewModal = React.useCallback(() => {
    setOptions((options) => (options.enabled = !enabled));
    scrollIntoNode();
    setSpeaker(speaker);
  }, [setOptions, scrollIntoNode, setSpeaker, enabled, speaker]);

  return (
    <>
      <SettingsTitleWrapper
        title={t("preference-panel.speakers.speaker")}
        tooltip={{
          description: t("preference-panel.speakers.navigate-to-speakers"),
          image: speakersTooltipPreview,
        }}
      />
      <p className="mb-4 cursor-default  text-xs font-medium text-gray-700">
        {t("preference-panel.speakers.speaker-picture")}
      </p>
      <Image src={speaker.image_url} alt={speaker.name} />
      <p className="mb-2 cursor-default text-xs font-medium leading-4 text-gray-700">
        {t("preference-panel.speakers.full-name")}
      </p>
      <p className="mb-6 cursor-default text-sm font-medium leading-6 text-gray-800">
        {speaker.name}
      </p>
      {speaker.headline && (
        <React.Fragment>
          <p className="mb-2 cursor-default text-xs font-medium leading-4 text-gray-700">
            {t("common.headline")}
          </p>
          <p className="cursor-default text-sm font-medium leading-6 text-gray-800">
            {speaker.headline}
          </p>
        </React.Fragment>
      )}
      {speaker.social?.linkedin_url && (
        <React.Fragment>
          <p className="mb-2 mt-6 cursor-default text-xs font-medium leading-4 text-gray-700">
            LinkedIn
          </p>
          <Link
            aria-label="speaker-linkedin"
            href={speaker.social.linkedin_url}
            target="_blank"
            rel="noreferrer"
          >
            {speaker.social.linkedin_url}
          </Link>
        </React.Fragment>
      )}
      {speaker.social?.twitter_url && (
        <React.Fragment>
          <p className="mb-2 mt-6 cursor-default text-xs font-medium text-gray-700">
            Twitter
          </p>
          <Link
            aria-label="speaker-twitter"
            href={speaker.social.twitter_url}
            target="_blank"
            rel="noreferrer"
          >
            {speaker.social.twitter_url}
          </Link>
        </React.Fragment>
      )}
      {speaker.social?.website_url && (
        <React.Fragment>
          <p className="mb-2 mt-6 cursor-default text-xs font-medium text-gray-700">
            Website
          </p>
          <Link
            aria-label="speaker-website"
            href={speaker.social.website_url}
            target="_blank"
            rel="noreferrer"
          >
            {speaker.social.website_url}
          </Link>
        </React.Fragment>
      )}
      <div className="mt-8">
        <SettingsTitleWrapper
          title={t("preference-panel.speakers.speaker-modal-settings")}
        >
          <SwitchInput
            label={t("preference-panel.speakers.show-bio-in-modal")}
            onChange={handleToggle}
            checked={props.isBioAvailable}
            data-testid="bio-modal-toggle"
          />
          <div className="mb-3 mt-5 flex justify-between">
            <p className="text-sm font-medium leading-6 text-gray-600">
              {t("preference-panel.speakers.speaker-bio")}
            </p>
            <Button
              variant="ghost"
              isInline
              size="tiny"
              className="text-xs leading-5"
              onClick={handlePreviewModal}
              data-testid="preview-bio-modal"
              disabled={!props.isBioAvailable}
            >
              {t("preference-panel.speakers.preview-modal")}
            </Button>
          </div>
          <BasicRichText
            config={{ removePlugins: ["S3Upload"] }}
            contentWrapper={ResizableContainer}
            data-testid="speaker-bio-editable-field"
            isDisabled={!props.isBioAvailable}
            isReadonly={!props.isBioAvailable}
            onChange={handleBioTextChange}
            text={speaker.bio || ""}
          />
        </SettingsTitleWrapper>
      </div>
    </>
  );
}
